import { motion } from "framer-motion";
import { Search, Route, HardHat, Handshake } from "lucide-react";
import { Reveal } from "./motion-primitives";
import { SectionLabel } from "./About";
import { useLang, Rich } from "@/i18n/LanguageContext";

const ICONS = [Search, Route, HardHat, Handshake];

export const Capabilities = () => {
  const { c } = useLang();
  return (
    <section id="capabilities" data-testid="section-capabilities" className="bg-paper2 px-6 py-24 text-ink md:px-[8vw] md:py-32">
      <Reveal>
        <SectionLabel n="03">{c.capabilities.label}</SectionLabel>
      </Reveal>
      <div className="mb-16 grid gap-8 md:mb-24 md:grid-cols-[1.25fr_0.75fr] md:gap-[10vw]">
        <Reveal>
          <h2 className="font-serif text-[clamp(44px,5.2vw,80px)] font-normal leading-[0.98] tracking-[-0.02em]">
            <Rich text={c.capabilities.title} />
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="pt-3 font-sans text-[15px] leading-[1.8] text-[#5e6966]">{c.capabilities.intro}</p>
          <div className="group mt-8 hidden overflow-hidden md:block">
            <img
              src="/images/osrelis-8.jpg"
              alt="Drilling rig illuminated at dusk"
              className="aspect-[4/3] w-full object-cover grayscale transition-[filter,transform] duration-[1200ms] group-hover:scale-105 group-hover:grayscale-0"
              data-testid="capabilities-image"
            />
          </div>
        </Reveal>
      </div>

      <div className="grid gap-4 md:grid-cols-2 md:gap-6">
        {c.capabilities.items.map((it, i) => {
          const num = `0${i + 1}`;
          const Icon = ICONS[i];
          return (
            <motion.article
              key={num}
              data-testid={`capability-${num}`}
              data-cursor
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-70px" }}
              transition={{ duration: 0.7, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className="group relative flex flex-col overflow-hidden border border-ink/15 bg-white/25 p-8 transition-colors duration-300 hover:bg-white/70 md:p-10"
            >
              <span className="absolute left-0 top-0 h-[2px] w-0 bg-teal transition-all duration-500 ease-out group-hover:w-full" />
              <div className="mb-8 flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-ink/20 text-ink transition-all duration-300 group-hover:-rotate-6 group-hover:border-teal group-hover:text-teal">
                  <Icon size={20} strokeWidth={1.5} />
                </span>
                <span className="font-serif text-[34px] leading-none text-[#c3c9c5] transition-colors duration-300 group-hover:text-teal">{num}</span>
              </div>
              <h3 className="mb-4 font-serif text-[26px] font-normal leading-tight transition-colors duration-300 group-hover:text-teal md:text-[30px]">{it.t}</h3>
              <p className="font-sans text-[13px] leading-[1.75] text-[#5e6966]">{it.d}</p>
              <span className="mt-7 inline-flex items-center gap-3 font-sans text-[10px] uppercase tracking-[0.16em] text-ink">
                <span className="h-px w-6 bg-ink/40 transition-all duration-500 group-hover:w-12 group-hover:bg-teal" />
                <span className="opacity-100 md:opacity-0 md:transition-opacity md:duration-300 md:group-hover:opacity-100">↗</span>
              </span>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
};
